import React, { useEffect, useState } from "react";
import { Card, CircleIconContainer, CurrencyPrice } from "@letsbit/milkylb";
import {
  ACCOUNT_PROVIDERS,
  MERK_ACTIVITY_TYPE,
  MerkActivity,
} from "@letsbit/malcolmlb";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import classNames from "classnames";
import { animated } from "@react-spring/web";
import InfiniteScroll from "react-infinite-scroll-component";

import { useTranslationConstants } from "hooks/utils/useTranslationConstants";
import { useFormatter } from "hooks/utils/useFormatter";
import { useAlpacaActivitiesScroll } from "./useAlpacaActivitiesScroll";
import { MiniLoading } from "components/MiniLoading";
import { Pill } from "components/Pill";
import { AlpacaActivityDetails } from "./components/AlpacaActivityDetails";

import { useAlpacaActivitiesUtils } from "./useAlpacaActivitiesUtils";
import { messages } from "./messages";
import { AlpacaActivitiesScrollProps } from "./types";

import "./styles.scss";

const HISTORY_CARD_HEIGHT = 68;

export const AlpacaActivitiesScroll: React.FC<AlpacaActivitiesScrollProps> = ({
  header,
  initialFilter,
  noChips,
  chipsAlign = "left",
  headerSticky,
  className,
  memberUid,
}) => {
  const intl = useIntl();
  const { formatValue } = useFormatter();

  const [height, setHeight] = useState<number | undefined>();
  const [pageToken, setPageToken] = useState<string | undefined>();

  const [currentActivity, setCurrentActivity] = useState<MerkActivity>();
  const { getMerkActivityStatus } = useTranslationConstants();

  const {
    listContainerRef,
    data,
    size,
    setSize,
    forwardedRef,
    itemsTransitions,
    hasMore,
    isLoading,
  } = useAlpacaActivitiesScroll<HTMLDivElement>(
    HISTORY_CARD_HEIGHT,
    {
      ...initialFilter,
      page_token: pageToken,
      provider: initialFilter?.provider || ACCOUNT_PROVIDERS.ALPACA,
      page_size: initialFilter?.page_size || "30",
    },
    false,
    memberUid,
  );

  enum MerkMovementsType {
    INVEST_AND_RESCUE = "investmentAndRescue",
    MOVEMENTS_BTW_ACCOUNT = "movementsBetweenAccounts",
  }

  const [selectedChip, setSelectedChip] = React.useState<MerkMovementsType>(
    MerkMovementsType.INVEST_AND_RESCUE,
  );

  const items = [
    {
      label: intl.formatMessage(messages.investmentAndRescue),
      value: MerkMovementsType.INVEST_AND_RESCUE,
    },
    {
      label: intl.formatMessage(messages.movementsBetweenAccounts),
      value: MerkMovementsType.MOVEMENTS_BTW_ACCOUNT,
    },
  ];

  const assetsHistoryFiltered = React.useMemo(() => {
    if (!noChips) {
      if (selectedChip === MerkMovementsType.MOVEMENTS_BTW_ACCOUNT) {
        return data?.filter(
          (operation) =>
            operation.type === MERK_ACTIVITY_TYPE.DEPOSIT ||
            operation.type === MERK_ACTIVITY_TYPE.WITHDRAWAL,
        );
      } else {
        return data?.filter(
          (operation) =>
            operation.type !== MERK_ACTIVITY_TYPE.DEPOSIT &&
            operation.type !== MERK_ACTIVITY_TYPE.WITHDRAWAL,
        );
      }
    } else {
      return data;
    }
  }, [noChips, selectedChip, MerkMovementsType.MOVEMENTS_BTW_ACCOUNT, data]);

  useEffect(() => {
    const observeTarget = listContainerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      // Assuming you want to track the dimensions of the first observed entry
      const { height } = entries[0].contentRect;

      if (height) {
        setHeight(height);
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    // Cleanup function to unobserve when the component unmounts
    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, [listContainerRef]);

  const { getMerkOperationInfo } = useAlpacaActivitiesUtils();

  return (
    <div
      className="HisotryInfiniteScroll fadeInSimple relative h-full grow"
      ref={listContainerRef}
    >
      {height && (
        <InfiniteScroll
          dataLength={assetsHistoryFiltered.length} // This is important field to render the next data
          next={() => {
            const lastItem = data[data.length - 1];
            setPageToken(lastItem?.id); // Use the last item's ID as the page token
            setSize(size + 1);
          }}
          style={{ overflowX: "hidden" }}
          hasMore={hasMore}
          loader={<MiniLoading />}
          height={height}
          className={classNames("list-none p-1", className)}
          endMessage={
            assetsHistoryFiltered.length === 0 ? (
              <p
                style={{ textAlign: "center" }}
                className="text-xs font-semibold text-[--milkylb-color-neutral-400]"
              >
                Tu cartera no tiene inversiones por el momento.
              </p>
            ) : (
              <p
                style={{ textAlign: "center" }}
                className="text-xs font-semibold text-[--milkylb-color-secondary-100]"
              >
                Llegaste al final de la lista
              </p>
            )
          }
        >
          {header}
          <div className="sticky -top-1 z-10 flex w-full flex-col">
            <div className="w-[calc(100% + 8px)] -ml-2 flex flex-row items-center justify-between border-b-4 border-l-8 border-r-8 border-t-8 border-white bg-white">
              {headerSticky && (
                <h3 className="mr-2 flex shrink-0 cursor-pointer items-center font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  {headerSticky}
                </h3>
              )}
              <div
                className={classNames(
                  "flex w-full",
                  chipsAlign === "right" ? "justify-end" : "justify-between",
                )}
              >
                {!noChips && (
                  <div className="flex flex-row gap-x-6">
                    {items.map((item) => (
                      <Pill
                        selected={selectedChip === item.value}
                        onClick={() => setSelectedChip(item.value)}
                      >
                        {item.label}
                      </Pill>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="divider-gradient h-[15px] w-full" />
          </div>
          {isLoading && data.length === 0 && <MiniLoading />}
          {assetsHistoryFiltered &&
            itemsTransitions((style, item) => {
              return (
                <animated.div style={style}>
                  <div ref={forwardedRef(item)} className="pr-2">
                    <Card
                      key={`${getMerkOperationInfo(item).currency}-${
                        item.type
                      }-${item.id}-${getMerkOperationInfo(item).date}`}
                      icon={
                        <CircleIconContainer
                          className="alpaca-activities-scroll__icon"
                          icon={getMerkOperationInfo(item).icon}
                        />
                      }
                      title={getMerkOperationInfo(item).title}
                      subtitle={dayjs(getMerkOperationInfo(item).date)?.format(
                        "DD.MM.YYYY",
                      )}
                      onClick={() => {
                        setCurrentActivity(item);
                      }}
                    >
                      <CurrencyPrice
                        className={classNames(
                          `alpaca-activities-scroll__price-${
                            getMerkActivityStatus(item).color
                          }`,
                        )}
                        price={formatValue(getMerkOperationInfo(item).amount)}
                        priceCurrency={
                          getMerkOperationInfo(item).currency?.toUpperCase() ||
                          ""
                        }
                        variation={getMerkActivityStatus(item).translate}
                      />
                    </Card>
                  </div>
                </animated.div>
              );
            })}
        </InfiniteScroll>
      )}
      <AlpacaActivityDetails
        activity={currentActivity}
        onClose={() => setCurrentActivity(undefined)}
      />
    </div>
  );
};

export default AlpacaActivitiesScroll;
